/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings
} from '@msdyn365-commerce/core';
import { SalesLine } from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey, getSimpleProducts, ProductInput } from '@msdyn365-commerce-modules/retail-actions';

import {
    getSalesOrdersByLookupCriteriaAsync,
    searchOrdersAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';
import { getCurrentAsync as getCurrentBusinessPartnerAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import {
    CustomerOrderType,
    OrderSearchCriteria,
    QueryResultSettings,
    SalesOrder,
    SalesOrderLookupBySalesId,
    SalesOrderLookupByTransactionId,
    SalesOrdersLookupCriteria,
    SimpleProduct,
    SortColumn
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { isNullOrWhiteSpace } from '@msdyn365-commerce-modules/utilities';
import { OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState } from '../components';

/**
 * IGetOrderHistoryWithSearchAndFiltersPaging interface.
 */
export interface IGetOrderHistoryWithSearchAndFiltersPaging {
    top: number;
    nextPageToken: string;
}

/**
 * IGetOrderHistoryWithSearchAndFiltersSorting interface.
 */
export interface IGetOrderHistoryWithSearchAndFiltersSorting {
    columnName: string;
    isDescending: boolean;
}

export interface IOrderHistoryWithSearchAndFilterError {
    statusCode?: number;
    errorMessageResource?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any;
}
/**
 * IOrderHistoryWithSearchAndFiltersResult interface.
 */
export interface IOrderHistoryWithSearchAndFiltersResult {
    salesOrders: SalesOrder[];
    products: SimpleProduct[];
    paging: IGetOrderHistoryWithSearchAndFiltersPaging;
    error?: IOrderHistoryWithSearchAndFilterError;
}

/**
 * IGetOrdersResult interface.
 */
interface IGetOrdersResult {
    salesOrders: SalesOrder[];
    paging: IGetOrderHistoryWithSearchAndFiltersPaging;
    error?: IOrderHistoryWithSearchAndFilterError;
}

/**
 * Calls the Retail API and returns the orders.
 * @param paging - The paging information to use in SearchOrders API.
 * @param sorting - The sorting information to use in SearchOrders API.
 * @param organizationWideSelectorState - The organization wide selector state.
 * @param orderSearchCriteria - The search criteria.
 * @returns Collection of SalesOrder.
 */
const getOrders = (
    paging: IGetOrderHistoryWithSearchAndFiltersPaging,
    sorting: IGetOrderHistoryWithSearchAndFiltersSorting | undefined,
    organizationWideSelectorState: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState,
    orderSearchCriteria: OrderSearchCriteria,
    currentContactPersonId?: string
) => async (ctx: IActionContext): Promise<IGetOrdersResult> => {
    let searchCriteria: OrderSearchCriteria;
    switch (organizationWideSelectorState) {
        case OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.CurrentUser:
            if (currentContactPersonId) {
                searchCriteria = {
                    ...orderSearchCriteria,
                    CustomerAccountNumbers: [ctx.requestContext.user.customerAccountNumber ?? ''],
                    ContactPersonIds: [currentContactPersonId],
                    OrderType: CustomerOrderType.SalesOrder
                };
            } else {
                searchCriteria = {
                    ...orderSearchCriteria,
                    CustomerAccountNumber: ctx.requestContext.user.customerAccountNumber,
                    OrderType: CustomerOrderType.SalesOrder
                };
            }
            break;
        case OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.OrganizationWide:
            if (currentContactPersonId) {
                searchCriteria = {
                    ...orderSearchCriteria,
                    CustomerAccountNumbers: [ctx.requestContext.user.customerAccountNumber ?? ''],
                    OrderType: CustomerOrderType.SalesOrder
                };
            } else {
                const businessPartner = await getCurrentBusinessPartnerAsync({ callerContext: ctx });
                if (!businessPartner) {
                    throw new Error('Unable to get order history. Failed to retrieve business partner id.');
                }
                searchCriteria = {
                    ...orderSearchCriteria,
                    BusinessPartnerId: businessPartner.BusinessPartnerId,
                    OrderType: CustomerOrderType.SalesOrder
                };
            }
            break;
        case OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.ShowAll:
            searchCriteria = {
                ...orderSearchCriteria,
                OrderType: CustomerOrderType.SalesOrder
            };
            break;
        default:
            throw new Error('getSalesOrderWithHydrationsAction - Invalid OrganizationOrderHistorySelectorState passed');
    }

    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: paging.top,
            NextPageToken: paging.nextPageToken // this value will be overwritten in server side.
        }
    };

    if (sorting) {
        queryResultSettings.Sorting = {
            Columns: [
                {
                    ColumnName: sorting.columnName,
                    IsDescending: sorting.isDescending
                } as SortColumn
            ]
        };
    }

    const searchOrdersResults = searchOrdersAsync(
        {
            callerContext: ctx,
            bypassCache: 'get',
            queryResultSettings: queryResultSettings
        },
        searchCriteria,
        paging.nextPageToken
    );

    const errorDetails: IOrderHistoryWithSearchAndFilterError = {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const searchOrdersResponse: SalesOrder[] = await searchOrdersResults.catch((error: any) => {
        errorDetails.statusCode = error?.statusCode;
        errorDetails.errorMessageResource = error?.message;
        errorDetails.error = error;
        return [];
    });

    const nextLinkUrl = searchOrdersResults.metadata?.others?.nextLink;
    const nextPageLink = nextLinkUrl !== undefined ? new URL(nextLinkUrl).searchParams.get('nextPageToken') : undefined;
    const newNextPageToken = nextPageLink !== null ? nextPageLink : undefined;

    const salesOrdersLookupCriteria: SalesOrdersLookupCriteria = {};
    salesOrdersLookupCriteria.SalesOrderLookups = searchOrdersResponse?.map((order: SalesOrder) => {
        if (order.SalesId && !isNullOrWhiteSpace(order.SalesId)) {
            const salesOrderLookupBySalesId = {
                SalesId: order.SalesId,
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.SalesOrderLookupBySalesId'
            } as SalesOrderLookupBySalesId;
            return salesOrderLookupBySalesId;
        } else if (order.Id && !isNullOrWhiteSpace(order.Id)) {
            const salesOrderLookupByTransactionId = {
                TransactionId: order.Id,
                ChannelId: order.ChannelId,
                StoreId: order.StoreId,
                TerminalId: order.TerminalId,
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.SalesOrderLookupByTransactionId'
            } as SalesOrderLookupByTransactionId;
            return salesOrderLookupByTransactionId;
        } else {
            throw new Error('Invalid input - There are no orders to view.');
        }
    });

    const orderHistoryResults = getSalesOrdersByLookupCriteriaAsync(
        {
            callerContext: ctx,
            bypassCache: 'get'
        },
        salesOrdersLookupCriteria
    );

    const orderHistoryResponse = await orderHistoryResults;

    // organize the order history by the order of the search results
    const orderHistoryResponseMap = new Map<string, SalesOrder>();
    orderHistoryResponse.forEach(order => {
        if (order.SalesId && !isNullOrWhiteSpace(order.SalesId)) {
            orderHistoryResponseMap.set(order.SalesId, order);
        }

        if (order.Id && !isNullOrWhiteSpace(order.Id)) {
            const key = `${order.Id}_${order.ChannelId}_${order.StoreId}_${order.TerminalId}`;
            orderHistoryResponseMap.set(key, order);
        }
    });

    const orderedOrderHistoryResponse = searchOrdersResponse
        ?.map((order: SalesOrder) => {
            let key: string;
            if (order.SalesId && !isNullOrWhiteSpace(order.SalesId)) {
                key = order.SalesId;
            } else if (order.Id && !isNullOrWhiteSpace(order.Id)) {
                key = `${order.Id}_${order.ChannelId}_${order.StoreId}_${order.TerminalId}`;
            } else {
                throw new Error('Invalid order - Missing key (SalesId / Id).');
            }
            return orderHistoryResponseMap.get(key);
        })
        .filter(order => order !== undefined);

    return {
        salesOrders: orderedOrderHistoryResponse,
        error: errorDetails,
        paging: {
            top: paging.top,
            nextPageToken: newNextPageToken || ''
        }
    } as IGetOrdersResult;
};

/**
 * Calls the Retail API and returns the products.
 * @param productIds - Collection of products identifiers.
 * @param channelId - Channel identifier.
 * @returns Collections of simple products.
 */
const getProducts = (productIds: number[] = [], channelId?: number) => (ctx: IActionContext): Promise<SimpleProduct[]> => {
    const productInputs = productIds.map(
        productId => new ProductInput(productId, ctx.requestContext.apiSettings, channelId, undefined, ctx.requestContext)
    );
    return getSimpleProducts(productInputs, ctx);
};

/**
 *  Action input.
 */
export class GetOrderHistoryWithSearchAndFiltersInput implements IActionInput {
    public paging: IGetOrderHistoryWithSearchAndFiltersPaging;

    public sorting?: IGetOrderHistoryWithSearchAndFiltersSorting;

    public organizationWideSelectorState: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState;

    public orderSearchCriteria: OrderSearchCriteria;

    public currentContactPersonId?: string;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(
        paging: IGetOrderHistoryWithSearchAndFiltersPaging,
        apiSettings: ICommerceApiSettings,
        organizationWideSelectorState?: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState,
        orderSearchCriteria?: OrderSearchCriteria,
        sorting?: IGetOrderHistoryWithSearchAndFiltersSorting,
        currentContactPersonId?: string
    ) {
        this.apiSettings = apiSettings;
        this.paging = paging;
        this.sorting = sorting;
        this.organizationWideSelectorState =
            organizationWideSelectorState || OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.CurrentUser;
        this.orderSearchCriteria = orderSearchCriteria || ({} as OrderSearchCriteria);
        this.currentContactPersonId = currentContactPersonId;
    }

    public getCacheKey = () => buildCacheKey('OrderHistoryWithSearchAndFilters', this.apiSettings);

    /**
     * GetCacheObjectType.
     * @returns String.
     */
    public getCacheObjectType = () => 'OrderHistoryWithSearchAndFilters';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * Splits product ids from the given orders into lists by their channel id.
 * @param {SalesOrder[]} salesOrders Orders with the products.
 * @param {number} currentChannelId Channel id to use by default if no channel id is provided for a product.
 * @returns {{ [x: number]: number[] }} A dictionary where the key is a channel id,
 * and the value is a list of product ids in which all items correspond to the key channel id.
 * @remark The list of product ids is always non-empty.
 */
const splitProductsByChannelId = (salesOrders: SalesOrder[], currentChannelId: number): { [x: number]: number[] } => {
    const productIdsByChannel: { [x: number]: number[] } = {};

    salesOrders.forEach(salesOrder =>
        salesOrder?.SalesLines?.forEach(line => {
            const orderProductId = line.ProductId || 0;
            const orderChannelId = salesOrder.ChannelId || currentChannelId;
            if (!productIdsByChannel[orderChannelId]) {
                productIdsByChannel[orderChannelId] = [];
            }
            productIdsByChannel[orderChannelId].push(orderProductId);
        })
    );

    return productIdsByChannel;
};

/**
 * Get sales orders given search and filters options.
 * @param input - GetOrderHistoryWithSearchAndFiltersInput.
 * @param context - Channel context.
 * @returns - Order History props.
 */
export async function getGetOrderHistoryWithSearchAndFiltersAction(
    input: GetOrderHistoryWithSearchAndFiltersInput,
    context: IActionContext
): Promise<IOrderHistoryWithSearchAndFiltersResult> {
    if (!context) {
        throw new Error('getSalesOrderWithHydrationsAction - Action context cannot be null/undefined');
    }
    const channelId = context.requestContext.apiSettings.channelId;
    const response: IGetOrdersResult = await getOrders(
        input.paging,
        input.sorting,
        input.organizationWideSelectorState,
        input.orderSearchCriteria,
        input.currentContactPersonId
    )(context);
    const salesOrders: SalesOrder[] = response.salesOrders;
    const paging: IGetOrderHistoryWithSearchAndFiltersPaging = response.paging;

    salesOrders.map(salesOrder => {
        return (salesOrder.SalesLines = salesOrder.SalesLines?.filter((saleline: SalesLine) => !saleline.IsVoided));
    });

    if (!salesOrders || salesOrders.length === 0) {
        return {
            salesOrders: [],
            products: [],
            paging: paging,
            error: response.error
        };
    }

    // Splits the data by channel ids so that the products from different channels can be processed separately.
    const productIdsByChannelId = splitProductsByChannelId(salesOrders, channelId);

    // Promise that retrieves information about the products for each channel id.
    // Note, the list of product ids should not be empty
    // as it will generate an empty request which will force the promise to fail.
    const getProductsPromise = Object.entries(productIdsByChannelId).map(([entryChannelId, entryProductIdsList]) => {
        const getProductsCall = getProducts(entryProductIdsList, Number(entryChannelId));
        return getProductsCall(context);
    });

    return Promise.all(getProductsPromise)
        .then(
            (productList): IOrderHistoryWithSearchAndFiltersResult => {
                const products = productList.reduce((memo, list) => {
                    return [...memo, ...list];
                }, []);
                return {
                    salesOrders,
                    products,
                    paging: paging,
                    error: response.error
                };
            }
        )
        .catch(error => {
            context.telemetry.exception(error);
            context.telemetry.debug('Failed to get products');
            throw error;
        });
}

export const getGetOrderHistoryWithSearchAndFiltersActionDataAction = createObservableDataAction<IOrderHistoryWithSearchAndFiltersResult>({
    id: '@msdyn365-commerce-modules/order-history-with-search-and-filters/get-order-history-with-search-and-filters',
    action: getGetOrderHistoryWithSearchAndFiltersAction as IAction<IOrderHistoryWithSearchAndFiltersResult>
});

export default getGetOrderHistoryWithSearchAndFiltersActionDataAction;
